import React, { useState } from 'react';
import { IoEyeOutline } from "react-icons/io5";
import { FiEyeOff } from "react-icons/fi";
import { Link, useNavigate } from 'react-router-dom';

function LoginForm() {
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const navigate = useNavigate();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);

        try {
            const res = await fetch('https://wa-api.globebot.io/auth/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    email: formData.email,
                    password: formData.password
                })
            });

            const data = await res.json();

            if (res.ok) {
                setSuccess('Login successful!');
                setFormData({ email: '', password: '' });
                if (data.token) {
                    localStorage.setItem('token', data.token);
                }
                if (data.dashboard) {
                    navigate('/');
                } else {
                    navigate('/');
                }
            } else {
                setError(data.error || 'Login failed. Please check your credentials.');
            }
        } catch (err) {
            setError('Login failed. Network error or server unreachable.');
        }
        setLoading(false);
    };

    return (
        <div className="absolute w-[480px] flex justify-center items-center py-8 px-2 md:px-0 min-h-[550px]">
            {/* Decorative background glow */}
            <div className="absolute -z-10 top-0 left-1/2 transform -translate-x-1/2 blur-3xl opacity-30 pointer-events-none w-[350px] h-[350px] bg-gradient-to-tr from-[#3fc5aa] via-[#31b07b] to-[#326ea0] rounded-full" />
            <div className="absolute -z-10 bottom-0 right-10 blur-2xl w-[200px] h-[220px] opacity-20 pointer-events-none bg-gradient-to-br from-[#c471f5] via-[#12fff7] to-[#fff]" />
            <div className="absolute -z-10 left-8 top-64 blur-2xl w-[120px] h-[120px] opacity-15 pointer-events-none bg-gradient-to-tr from-[#f99cff] to-[#1cb5e0]" />

            <div className="bg-white/95 rounded-3xl shadow-2xl w-full max-w-[990px] mx-auto py-10 px-6 relative overflow-hidden border-[1.5px] border-[#e3eeff]">
                <div className="absolute -z-10 left-3 top-3 w-28 h-28 bg-gradient-to-br from-[#31b07b]/30 to-[#e3eeff]/10 rounded-full blur-2xl"></div>
                <div className="absolute -z-10 right-1 -bottom-9 w-32 h-32 bg-gradient-to-tr from-[#fffae5]/40 to-[#c471f5]/20 rounded-full blur-2xl"></div>

                <div className="flex flex-col items-center mb-4">
                    <h1 className="font-black text-[22px] text-transparent bg-clip-text bg-gradient-to-br from-[#3fc5aa] to-[#326ea0] drop-shadow-sm animate-textMove">
                        globeomni.com
                    </h1>
                    <span className="text-[12px] text-gray-400 font-medium tracking-widest mb-1">Member Portal</span>
                </div>
                <form
                    className="flex flex-col gap-5"
                    onSubmit={handleSubmit}
                    autoComplete="off"
                >
                    <div className="flex flex-col gap-1 items-center">
                        <h2 className="text-[#22223b] text-2xl md:text-3xl font-extrabold leading-[1.2] mb-0">
                            Sign In
                        </h2>
                        <div className="text-gray-600 font-medium text-xs md:text-sm flex items-center gap-2">
                            <span>Don't have an account?</span>
                            <Link
                                to="/signup"
                                className="text-[#3fc5aa] underline font-semibold hover:text-[#265041] transition-colors duration-150"
                            >
                                Register here!
                            </Link>
                        </div>
                    </div>

                    {/* Email Field */}
                    <div className="relative w-full transition-all">
                        <input
                            type="email"
                            name="email"
                            placeholder="Email address"
                            className="peer w-full h-12 placeholder-gray-400 px-4 rounded-xl bg-[#f7fafc] border border-[#d3e0ee] focus:outline-none focus:border-[#31b07b] text-[#22223b] font-semibold shadow-sm transition-all duration-200"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            autoComplete="email"
                        />
                        <label className="absolute left-4 top-2 text-xs text-[#576071] font-light pointer-events-none opacity-70 peer-focus:text-[#3fc5aa] peer-focus:opacity-100 transition-all duration-200"></label>
                    </div>

                    {/* Password Field */}
                    <div className="relative flex items-center transition-all">
                        <input
                            type={showPassword ? "text" : "password"}
                            name="password"
                            placeholder="Password"
                            className="peer w-full h-12 px-4 rounded-xl bg-[#f7fafc] border border-[#d3e0ee] focus:outline-none focus:border-[#31b07b] text-[#22223b] font-semibold shadow-sm transition-all duration-200"
                            value={formData.password}
                            onChange={handleChange}
                            required
                            autoComplete="current-password"
                        />
                        <button
                            type="button"
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#576071] hover:text-[#3fc5aa] focus:outline-none"
                            tabIndex={-1}
                            onClick={() => setShowPassword((val) => !val)}
                            aria-label={showPassword ? 'Hide Password' : 'Show Password'}
                        >
                            {showPassword ? <IoEyeOutline size={22} /> : <FiEyeOff size={22} />}
                        </button>
                    </div>

                    {/* Checkbox and Forgot */}
                    <div className="flex flex-row items-center justify-between mt-1 gap-4">
                        <label className="flex items-center cursor-pointer select-none text-gray-600 text-xs font-medium">
                            <input
                                type="checkbox"
                                id="KeepmeSignedin"
                                className="w-4 h-4 accent-[#31b07b] rounded-lg mr-2"
                            />
                            Keep me signed in
                        </label>
                        <span
                            className="text-[#3fc5aa] text-xs font-semibold cursor-pointer hover:underline transition-all"
                            tabIndex={0}
                        >
                            Forgot password?
                        </span>
                    </div>

                    {error && (
                        <div className="w-full text-center bg-[#ffeaea] py-2 rounded-lg text-red-600 font-medium text-xs border border-red-100 drop-shadow-sm">
                            {error}
                        </div>
                    )}
                    {success && (
                        <div className="w-full text-center bg-[#eaffea] py-2 rounded-lg text-green-600 font-medium text-xs border border-green-100 drop-shadow-sm">
                            {success}
                        </div>
                    )}

                    <button
                        className="mt-2 w-full py-3 rounded-xl font-bold bg-gradient-to-r from-[#31b07b] via-[#3fc5aa] to-[#326ea0] text-white tracking-wide shadow-lg hover:from-[#3fc5aa] hover:to-[#265041] hover:shadow-xl transition-all duration-200 focus:ring-2 focus:ring-[#3fc5aa] focus:outline-none"
                        type="submit"
                        disabled={loading}
                    >
                        {loading ? (
                            <span className="flex items-center justify-center gap-2">
                                <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                Signing in...
                            </span>
                        ) : (
                            "Sign in"
                        )}
                    </button>

                    <div className="w-full h-[1px] bg-gradient-to-r from-[#31b07b22] via-[#57607144] to-[#31b07b22] my-2"></div>

                    <div className="flex items-center justify-center gap-3 my-1 text-xs text-gray-500 font-medium">
                        <button
                            type="button"
                            className="focus:outline-none underline text-[#3fc5aa] hover:text-[#326ea0] transition-colors"
                        >
                            Privacy
                        </button>
                        <span className="mx-2 select-none">|</span>
                        <button
                            type="button"
                            className="focus:outline-none underline text-[#3fc5aa] hover:text-[#326ea0] transition-colors"
                        >
                            Terms & Conditions
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default LoginForm;
